/**
 * Compact 4-across glance strip at the top of a dock tab.
 *
 * Each cell is a colored number and a short caps label. Tabs pick their own
 * indicators from data they already have — this file only draws the grid.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

const MUTED = '#8a939f';
const EDGE = '#2a3038';
const PAPER = 'rgba(255,255,255,0.035)';

export interface DockStat {
  label: string;
  value: string | number;
  color?: string;
  icon?: React.ReactNode;
}

export function formatStat(value: string | number): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString();
  }
  return String(value);
}

export const DockStatStrip: React.FC<{ items: DockStat[]; embedded?: boolean }> = ({
  items, embedded,
}) => {
  if (items.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        columnGap: 0.5,
        rowGap: 0.85,
        px: embedded ? 0.15 : 0.9,
        py: embedded ? 0.15 : 0.85,
        mb: embedded ? 0.85 : 1.1,
        borderRadius: embedded ? 0 : 1.25,
        bgcolor: embedded ? 'transparent' : PAPER,
        border: embedded ? 'none' : `1px solid ${EDGE}`,
      }}
    >
      {items.map((item) => {
        const color = item.color || '#e8ebee';
        return (
          <Box key={item.label} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
              {item.icon && (
                <Box sx={{
                  display: 'inline-flex',
                  color,
                  '& .MuiSvgIcon-root': { fontSize: 13 },
                  flexShrink: 0,
                }}
                >
                  {item.icon}
                </Box>
              )}
              <Typography
                noWrap
                sx={{
                  fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
                  fontSize: '0.92rem',
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                  color,
                }}
              >
                {formatStat(item.value)}
              </Typography>
            </Box>
            <Typography
              noWrap
              sx={{
                mt: 0.2,
                fontSize: '0.48rem',
                fontWeight: 700,
                letterSpacing: 0.55,
                textTransform: 'uppercase',
                color: MUTED,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default DockStatStrip;
